import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Activity, CheckCircle, AlertTriangle, XCircle, ArrowRight } from 'lucide-react';
import '../../styles/design-system.css';

const HealthDiagnosis = ({ onClose }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);

  const questions = [
    {
      id: 1,
      category: 'Controle',
      question: 'Você sabe exatamente quanto ganha e quanto gasta por mês?',
      options: [
        { text: 'Sim, tenho tudo anotado', points: 10 },
        { text: 'Tenho uma ideia aproximada', points: 6 },
        { text: 'Não sei ao certo', points: 3 },
        { text: 'Não faço ideia', points: 0 }
      ]
    },
    {
      id: 2,
      category: 'Dízimo',
      question: 'Como é sua relação com o dízimo?',
      options: [
        { text: 'Dizimo fielmente todos os meses', points: 10 },
        { text: 'Dizimo quando sobra', points: 5 },
        { text: 'Raramente consigo dizimar', points: 2 },
        { text: 'Não pratico o dízimo', points: 0 }
      ]
    },
    {
      id: 3,
      category: 'Reserva',
      question: 'Você tem reserva de emergência?',
      options: [
        { text: 'Sim, para 6 meses ou mais', points: 10 },
        { text: 'Sim, para 3 a 6 meses', points: 7 },
        { text: 'Tenho algo, mas pouco', points: 4 },
        { text: 'Não tenho reserva', points: 0 }
      ]
    },
    {
      id: 4,
      category: 'Dívidas',
      question: 'Qual sua situação com dívidas?',
      options: [
        { text: 'Não tenho dívidas', points: 10 },
        { text: 'Tenho apenas financiamento planejado', points: 7 },
        { text: 'Tenho algumas dívidas sob controle', points: 4 },
        { text: 'Estou endividado(a) e preocupado(a)', points: 0 }
      ]
    },
    {
      id: 5,
      category: 'Investimentos',
      question: 'Você investe regularmente?',
      options: [
        { text: 'Sim, todo mês sem falta', points: 10 },
        { text: 'Invisto quando sobra dinheiro', points: 5 },
        { text: 'Já investi, mas parei', points: 2 },
        { text: 'Nunca investi', points: 0 }
      ]
    },
    {
      id: 6,
      category: 'Planejamento',
      question: 'Você tem metas financeiras definidas?',
      options: [
        { text: 'Sim, com prazos e valores', points: 10 },
        { text: 'Tenho metas, mas vagas', points: 6 },
        { text: 'Penso nisso às vezes', points: 3 },
        { text: 'Não tenho metas', points: 0 }
      ]
    },
    {
      id: 7,
      category: 'Educação',
      question: 'Você busca conhecimento sobre finanças?',
      options: [
        { text: 'Sim, constantemente', points: 10 },
        { text: 'De vez em quando', points: 6 },
        { text: 'Raramente', points: 3 },
        { text: 'Nunca', points: 0 }
      ]
    },
    {
      id: 8,
      category: 'Comportamento',
      question: 'Como você lida com compras por impulso?',
      options: [
        { text: 'Raramente compro por impulso', points: 10 },
        { text: 'Às vezes me controlo', points: 6 },
        { text: 'Tenho dificuldade em resistir', points: 3 },
        { text: 'Compro muito por impulso', points: 0 }
      ]
    },
    {
      id: 9,
      category: 'Renda',
      question: 'Sua renda cobre suas despesas com folga?',
      options: [
        { text: 'Sim, sobra bastante', points: 10 },
        { text: 'Sim, sobra um pouco', points: 7 },
        { text: 'Fico no zero a zero', points: 3 },
        { text: 'Não, gasto mais do que ganho', points: 0 }
      ]
    },
    {
      id: 10,
      category: 'Futuro',
      question: 'Você pensa na aposentadoria?',
      options: [
        { text: 'Sim, já me preparo para isso', points: 10 },
        { text: 'Penso, mas não faço nada', points: 5 },
        { text: 'Acho que é cedo demais', points: 2 },
        { text: 'Não penso nisso', points: 0 }
      ]
    }
  ];

  const handleAnswer = (points) => {
    const newAnswers = [...answers, points];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResult(true);
    }
  };

  const calculateResult = () => {
    const total = answers.reduce((sum, a) => sum + a, 0);
    const maxPoints = questions.length * 10;
    const score = Math.round((total / maxPoints) * 100);

    let status, color, icon, message, recommendations;

    if (score >= 80) {
      status = 'Excelente';
      color = '#4CAF50';
      icon = CheckCircle;
      message = 'Parabéns! Você tem uma saúde financeira exemplar. Continue assim e ajude outros a alcançarem esse nível!';
      recommendations = [
        'Continue investindo e diversificando',
        'Considere aumentar suas ofertas e doações',
        'Compartilhe seu conhecimento com outros',
        'Pense em criar fontes de renda passiva'
      ];
    } else if (score >= 60) {
      status = 'Boa';
      color = '#8BC34A';
      icon = CheckCircle;
      message = 'Você está no caminho certo! Com alguns ajustes, pode alcançar a excelência financeira.';
      recommendations = [
        'Fortaleça sua reserva de emergência',
        'Aumente gradualmente seus investimentos',
        'Elimine compras por impulso',
        'Defina metas mais específicas'
      ];
    } else if (score >= 40) {
      status = 'Regular';
      color = '#FFC107';
      icon = AlertTriangle;
      message = 'Sua saúde financeira precisa de atenção. É hora de fazer mudanças importantes.';
      recommendations = [
        'Comece a anotar todos os gastos',
        'Priorize o dízimo como compromisso',
        'Crie uma reserva de emergência',
        'Busque conhecimento financeiro'
      ];
    } else {
      status = 'Crítica';
      color = '#F44336';
      icon = XCircle;
      message = 'Sua situação financeira precisa de atenção urgente. Mas não desanime - com orientação certa, a transformação é possível!';
      recommendations = [
        'Faça um diagnóstico completo das dívidas',
        'Busque renegociar juros e prazos',
        'Corte gastos supérfluos imediatamente',
        'Procure ajuda profissional'
      ];
    }

    return { score, status, color, icon, message, recommendations };
  };

  const result = showResult ? calculateResult() : null;
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="p-6 max-h-[80vh] overflow-y-auto">
      <div className="flex items-center gap-3 mb-6">
        <div 
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{ background: 'var(--accent-orange-200)' }}
        >
          <Activity size={24} style={{ color: 'var(--text-primary)' }} />
        </div>
        <div>
          <h3 className="heading-2">Diagnóstico de Saúde Financeira</h3>
          <p className="caption">Avaliação completa em 10 perguntas</p>
        </div>
      </div>

      {!showResult ? (
        <>
          {/* Progress */}
          <div className="mb-4">
            <div className="flex justify-between mb-2">
              <span className="caption">Pergunta {currentQuestion + 1} de {questions.length}</span>
              <span className="caption">{questions[currentQuestion].category}</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--border-light)' }}>
              <motion.div
                className="h-full rounded-full"
                style={{ background: 'var(--text-primary)' }}
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Question */}
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <h4 className="heading-3 mb-6">{questions[currentQuestion].question}</h4>
            
            <div className="space-y-3">
              {questions[currentQuestion].options.map((option, index) => (
                <motion.button
                  key={index}
                  onClick={() => handleAnswer(option.points)}
                  className="w-full p-4 rounded-xl text-left transition-all"
                  style={{ 
                    background: 'var(--bg-section)',
                    border: '2px solid transparent'
                  }}
                  whileHover={{ 
                    scale: 1.02,
                    background: 'var(--accent-purple-200)',
                    borderColor: 'var(--accent-purple-400)'
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="body-medium">{option.text}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        </>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          {/* Score Circle */}
          <div className="flex justify-center mb-6">
            <div 
              className="relative w-40 h-40 rounded-full flex items-center justify-center"
              style={{ 
                background: `conic-gradient(${result.color} ${result.score}%, var(--border-light) ${result.score}%)`
              }}
            >
              <div 
                className="w-32 h-32 rounded-full flex flex-col items-center justify-center"
                style={{ background: 'var(--bg-card)' }}
              >
                <span className="heading-hero">{result.score}</span>
                <span className="caption">pontos</span>
              </div>
            </div>
          </div>

          {/* Status */}
          <div 
            className="text-center p-4 rounded-xl mb-6"
            style={{ background: `${result.color}20` }}
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <result.icon size={24} style={{ color: result.color }} />
              <span className="heading-2" style={{ color: result.color }}>{result.status}</span>
            </div>
            <p className="body-small">{result.message}</p>
          </div>

          {/* Recommendations */}
          <div className="mb-6">
            <h4 className="body-medium font-medium mb-3">📋 Recomendações para você:</h4>
            <div className="space-y-2">
              {result.recommendations.map((rec, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 p-3 rounded-lg"
                  style={{ background: 'var(--bg-section)' }}
                >
                  <ArrowRight size={16} className="flex-shrink-0 mt-0.5" style={{ color: result.color }} />
                  <span className="body-small">{rec}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Biblical Quote */}
          <div className="voice-card accent-purple mb-4">
            <p className="caption italic">
              "Qual de vocês, se quiser construir uma torre, primeiro não se assenta e calcula o custo, 
              para ver se tem dinheiro suficiente para completá-la?" - Lucas 14:28
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => {
                setCurrentQuestion(0);
                setAnswers([]);
                setShowResult(false);
              }}
              className="btn-secondary flex-1"
            >
              Refazer
            </button>
            <button onClick={onClose} className="btn-primary flex-1">
              Quero Consultoria
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default HealthDiagnosis;
